import { EpubNavigator, EpubNavigatorListeners, EpubPreferences } from '@readium/navigator'
import type { BasicTextSelection, FrameClickEvent } from '@readium/navigator-html-injectables'
import { Link, Locator, LocatorText } from '@readium/shared'
import { useEffect, useMemo, useRef, useState } from 'react'

import { createAuthenticatedFetch } from '@/api/client'
import { useLatestRef } from '@/hooks/useLatestRef'

import {
  DEFAULT_READER_SETTINGS,
  getFontFamilyCSS,
  getThemeColors,
  ReaderSettings,
} from '../../components/reader-settings-types'
import { loadEpubPublication } from './epub-publication-loader'
import { EpubErrorDisplay, EpubLoadingOverlay } from './epub-shared-ui'

export type LocationInfo = {
  locator: Locator
  percentage: number
  atEnd: boolean
}

export type TextSelectionInfo = {
  /** Readium Locator (選択範囲の位置) */
  locator: Locator
  text: string
}

export type EpubViewerProps = {
  manifestUrl: string
  initialLocator?: Locator | null
  readerSettings?: ReaderSettings
  onClick?: () => void
  onTocLoaded?: (toc: Link[]) => void
  onNavigatorReady?: (navigator: EpubNavigator, positions: Locator[]) => void
  onLocationChange?: (location: LocationInfo) => void
  onTextSelected?: (selection: TextSelectionInfo) => void
  onSelectionCleared?: () => void
  onWritingModeDetected?: (isVertical: boolean) => void
  /** 本文の iframe が読み込まれたとき（流し読みの強調などを本文に適用する用） */
  onFrameLoaded?: (wnd: Window) => void
}

/** ReaderSettings → EpubPreferences 変換 */
const settingsToPreferences = (settings: ReaderSettings, isVertical = false): EpubPreferences => {
  const colors = getThemeColors(settings.theme)

  return new EpubPreferences({
    scroll: settings.flow !== 'paginated',
    fontSize: settings.fontSize / 16,
    lineHeight: settings.lineHeight,
    fontFamily: getFontFamilyCSS(settings.fontFamily),
    backgroundColor: colors.backgroundColor,
    textColor: colors.textColor,
    linkColor: colors.linkColor,
    scrollPaddingTop: isVertical ? settings.marginLeft : 40,
    scrollPaddingBottom: isVertical ? settings.marginRight : 40,
    scrollPaddingLeft: isVertical ? 40 : settings.marginLeft,
    scrollPaddingRight: isVertical ? 40 : settings.marginRight,
  })
}

export const EpubViewer = ({
  manifestUrl,
  initialLocator,
  readerSettings,
  onClick,
  onTocLoaded,
  onNavigatorReady,
  onLocationChange,
  onTextSelected,
  onSelectionCleared,
  onWritingModeDetected,
  onFrameLoaded,
}: EpubViewerProps) => {
  const viewerRef = useRef<HTMLDivElement>(null)
  const navigatorRef = useRef<EpubNavigator | null>(null)
  const isInitializedRef = useRef<string | null>(null)
  const readerSettingsRef = useRef(readerSettings)
  const hasTextSelectionRef = useRef(false)
  const isVerticalRef = useRef(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // コールバックをrefに保持（依存配列に入れず安定させる）
  const onClickRef = useLatestRef(onClick)
  const onTocLoadedRef = useLatestRef(onTocLoaded)
  const onNavigatorReadyRef = useLatestRef(onNavigatorReady)
  const onLocationChangeRef = useLatestRef(onLocationChange)
  const onTextSelectedRef = useLatestRef(onTextSelected)
  const onSelectionClearedRef = useLatestRef(onSelectionCleared)
  const onWritingModeDetectedRef = useLatestRef(onWritingModeDetected)
  const onFrameLoadedRef = useLatestRef(onFrameLoaded)

  useEffect(() => {
    readerSettingsRef.current = readerSettings
  }, [readerSettings])

  // 認証ヘッダー付きfetch（トークン自動付与）
  const authFetch = useMemo(() => createAuthenticatedFetch(), [])

  useEffect(() => {
    if (!manifestUrl || !viewerRef.current) return

    // 同じマニフェスト＋同じflowで初期化済みならスキップ
    const initKey = `${manifestUrl}::${readerSettings?.flow ?? 'scrolled'}`
    if (isInitializedRef.current === initKey && navigatorRef.current) return

    let destroyed = false
    let nav: EpubNavigator | null = null

    const initializeReadium = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // 既存のNavigatorを破棄
        if (navigatorRef.current) {
          await navigatorRef.current.destroy()
          navigatorRef.current = null
        }

        // 1. Publication構築（マニフェスト取得・パース・ポジション取得）
        const { publication, positions } = await loadEpubPublication(authFetch, manifestUrl)

        if (destroyed) return

        // 2. TOCをコールバック
        if (publication.toc && publication.toc.items.length > 0) {
          onTocLoadedRef.current?.(publication.toc.items)
        }

        // 3. EpubNavigator構築
        if (!viewerRef.current) return

        const listeners: EpubNavigatorListeners = {
          positionChanged: (locator: Locator) => {
            const totalProgression = locator.locations.totalProgression ?? 0
            const atEnd = totalProgression >= 0.99

            onLocationChangeRef.current?.({
              locator,
              percentage: totalProgression,
              atEnd,
            })
          },

          tap: (e: FrameClickEvent) => {
            if (hasTextSelectionRef.current) {
              hasTextSelectionRef.current = false
              onSelectionClearedRef.current?.()
              return true
            }
            if (e.interactiveElement) return false
            onClickRef.current?.()
            return true
          },

          click: (e: FrameClickEvent) => {
            if (hasTextSelectionRef.current) {
              hasTextSelectionRef.current = false
              onSelectionClearedRef.current?.()
              return true
            }
            if (e.interactiveElement) return false
            onClickRef.current?.()
            return true
          },

          textSelected: (selection: BasicTextSelection) => {
            if (!selection.text) return
            hasTextSelectionRef.current = true

            const currentLocator = navigatorRef.current?.currentLocator
            if (!currentLocator) return

            const selectionLocator = new Locator({
              href: currentLocator.href,
              type: currentLocator.type,
              title: currentLocator.title,
              locations: currentLocator.locations,
              text: new LocatorText({ highlight: selection.text }),
            })

            onTextSelectedRef.current?.({
              locator: selectionLocator,
              text: selection.text,
            })
          },

          frameLoaded: (wnd: Window) => {
            const doc = wnd.document
            if (!doc.body) return

            // writing-mode 検出
            const bodyStyle = wnd.getComputedStyle(doc.body)
            const writingMode = bodyStyle.writingMode
            const isVerticalWriting = writingMode?.includes('vertical') ?? false
            if (isVerticalWriting !== isVerticalRef.current) {
              isVerticalRef.current = isVerticalWriting
              onWritingModeDetectedRef.current?.(isVerticalWriting)
            }

            // 縦書き(vertical-rl/lr)時にReadiumCSSのスクロールモードが
            // body に max-width + overflow:clip を設定しており、
            // 水平スクロールが機能しない問題を修正する
            if (isVerticalWriting && !doc.getElementById('readium-vertical-scroll-fix')) {
              try {
                const style = doc.createElement('style')
                style.id = 'readium-vertical-scroll-fix'
                style.dataset.readium = 'true'
                style.textContent = `
                  html {
                    overflow-x: auto !important;
                    overflow-y: hidden !important;
                    scrollbar-width: thin !important;
                  }
                  html::-webkit-scrollbar {
                    display: block !important;
                    height: 8px !important;
                  }
                  html::-webkit-scrollbar-thumb {
                    background-color: rgba(128,128,128,0.4) !important;
                    border-radius: 4px !important;
                  }
                  html::-webkit-scrollbar-track {
                    background-color: transparent !important;
                  }
                  body {
                    width: auto !important;
                    max-width: none !important;
                    overflow: visible !important;
                  }
                `
                doc.head.appendChild(style)
              } catch (e) {
                console.warn('[EpubViewer] frameLoaded CSS injection failed:', e)
              }
            }

            onFrameLoadedRef.current?.(wnd)
          },
          zoom: () => {},
          miscPointer: () => {},
          scroll: () => {},
          customEvent: () => {},
          handleLocator: () => false,
          contentProtection: () => {},
          contextMenu: () => {},
          peripheral: () => {},
        }

        const effectiveSettings = readerSettings ?? DEFAULT_READER_SETTINGS
        const preferences = settingsToPreferences(effectiveSettings, isVerticalRef.current)

        // initialLocatorがpositionsリストと整合しない場合は無視
        let safeLocator = initialLocator ?? undefined
        if (safeLocator && positions.length > 0) {
          const hrefMatch = positions.some((p) => p.href === safeLocator!.href)
          const positionValid =
            safeLocator.locations.position == null || safeLocator.locations.position <= positions.length
          if (!hrefMatch || !positionValid) {
            console.warn('[EpubViewer] initialLocator not found in positions, resetting')
            safeLocator = undefined
          }
        }

        // Navigatorの構築・ロードを試み、initialLocatorが無効ならフォールバック
        const createNavigator = (locator?: Locator) =>
          new EpubNavigator(viewerRef.current!, publication, listeners, positions, locator, {
            preferences,
            defaults: {},
          })

        try {
          nav = createNavigator(safeLocator)
          if (destroyed) return
          await nav.load()
        } catch (locatorErr) {
          if (safeLocator) {
            console.warn('[EpubViewer] initialLocator caused error, retrying without it:', locatorErr)
            if (nav) await nav.destroy().catch(() => {})
            nav = createNavigator(undefined)
            if (destroyed) return
            await nav.load()
          } else {
            throw locatorErr
          }
        }

        if (destroyed) {
          await nav.destroy()
          return
        }

        navigatorRef.current = nav
        isInitializedRef.current = initKey
        setIsLoading(false)

        onNavigatorReadyRef.current?.(nav, positions)
      } catch (err) {
        console.error('EPUB初期化に失敗しました', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
        setIsLoading(false)
        isInitializedRef.current = null
      }
    }

    initializeReadium()

    return () => {
      destroyed = true
      if (nav) {
        nav.destroy().catch(console.error)
        navigatorRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifestUrl, authFetch, readerSettings?.flow])

  // 設定変更時にPreferencesを再適用
  useEffect(() => {
    if (!navigatorRef.current || !readerSettings) return

    const preferences = settingsToPreferences(readerSettings, isVerticalRef.current)
    navigatorRef.current.submitPreferences(preferences).catch(console.error)
  }, [readerSettings])

  if (error) {
    return <EpubErrorDisplay error={error} />
  }

  return (
    <>
      <EpubLoadingOverlay isLoading={isLoading} />
      <div
        ref={viewerRef}
        className="absolute inset-0 w-full h-full [&_iframe.readium-navigator-iframe]:w-full! [&_iframe.readium-navigator-iframe]:h-full!"
      />
    </>
  )
}
